// Notification utility functions for frontend-only notification system
import Storage from './storage';

const NOTIFICATIONS_KEY = 'notifications_data';
const READ_NOTIFICATIONS_KEY = 'read_notifications';

/**
 * Get all read notification IDs from localStorage
 */
export const getReadNotifications = async () => {
  try {
    const read = await Storage.getItem(READ_NOTIFICATIONS_KEY);
    return read ? JSON.parse(read) : [];
  } catch (error) {
    console.warn('Error loading read notifications:', error);
    return [];
  }
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (notificationId) => {
  try {
    const read = await getReadNotifications();
    if (!read.includes(notificationId)) {
      read.push(notificationId);
      await Storage.setItem(READ_NOTIFICATIONS_KEY, JSON.stringify(read));
    }
  } catch (error) {
    console.warn('Error marking notification as read:', error);
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (notificationIds) => {
  try {
    const read = await getReadNotifications();
    notificationIds.forEach(id => {
      if (!read.includes(id)) {
        read.push(id);
      }
    });
    await Storage.setItem(READ_NOTIFICATIONS_KEY, JSON.stringify(read));
  } catch (error) {
    console.warn('Error marking all notifications as read:', error);
  }
};

/**
 * Check if a notification is read
 */
export const isNotificationRead = async (notificationId) => {
  const read = await getReadNotifications();
  return read.includes(notificationId);
};

/**
 * Format time ago (e.g., "2 minutes ago", "1 hour ago", "Yesterday")
 */
export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Just now';
  
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return 'Just now';
  } else if (minutes < 60) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return `${days} days ago`;
  } else {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  }
};

/**
 * Group notifications by time period
 */
export const groupNotificationsByTime = (notifications) => {
  const now = Date.now();
  const today = new Date().setHours(0, 0, 0, 0);
  const yesterday = today - 24 * 60 * 60 * 1000;
  const thisWeek = today - 7 * 24 * 60 * 60 * 1000;

  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: []
  };

  notifications.forEach(notification => {
    const timestamp = notification.timestamp || notification.createdAt || 0;
    
    if (timestamp >= today) {
      groups.today.push(notification);
    } else if (timestamp >= yesterday) {
      groups.yesterday.push(notification);
    } else if (timestamp >= thisWeek) {
      groups.thisWeek.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  return groups;
};

/**
 * Generate notification ID from pickup
 */
export const generateNotificationId = (pickupId, type = 'pickup') => {
  return `notification-${type}-${pickupId}`;
};

